import React from 'react';
import {
    Card,
    ExamStatus,
    SmsSignModal,
    ExamDetailsTabs,
} from 'components';
import { ExamDetailsConfirmationStatus } from '../../components/ExamDetailsConfirmationStatus';
import { ExamApplication, IExamDetails } from '../../api/ApiClient';
import { connect } from 'react-redux';
import { Dispatch, IRootState } from '../../redux/store/configureStore';
import { useLoading, useLocalErrorHandler } from 'hooks';
import { useHistory } from 'react-router';
import { Button } from 'react-bootstrap';

const ExamDetailsContainer: React.FC<Props> = (
  {
      examId,
      onFetchExamDetails,
      onFetchExamApplications,
      onClearExamApplications,
      onClearExamDetails,
      setErrorHandler,
      examDetails,
      examApplicationsList,
      apiError,
      auth,
      smsCodeStatus,
      ...props
  },
) => {
    const { push, goBack } = useHistory();
    useLocalErrorHandler(setErrorHandler);

    const [sendSmsProcessing, sendCode] = useLoading(async (captcha?: string) => props.sendCodeForApproveExam({ examId, stage: 1, body: { captcha } }));
    const [showSendSms, setShowSendSms] = React.useState(false);
    const [processing, setProcessing] = React.useState(false);
    const [selectedApplicant, setSelectedApplicant] = React.useState<ExamApplication | null>(null);
    React.useEffect(() => {
        const fetch = async () => {
            setProcessing(true);
            await onFetchExamDetails(examId);
            await handleListTableChange();
            setProcessing(false);
        };

        fetch();

        return () => {
            onClearExamApplications();
            onClearExamDetails();
        };
    }, [examId]);

    React.useEffect(() => {
        let interval: NodeJS.Timeout | null = null;

        if (examDetails && examDetails.status === ExamStatus.Staging) {
            interval = setInterval(async () => {
                await onFetchExamApplications({ examId, pageSize: 500 });
            }, 20 * 1000);
        }

        return () => {
            if (interval) {
                clearInterval(interval);
            }
        };
    }, [examDetails]);


    const handleListTableChange = async (searchField: string | undefined = '', searchTerm: string | undefined = '') => {
        await onFetchExamApplications({
            examId,
            searchField,
            searchTerm,
            pageSize: 500,
            sortOrder: undefined,
            sortField: undefined,
        });
    };

    React.useEffect(() => {
        if (selectedApplicant) {
            const applicant = examApplicationsList && examApplicationsList.items.find(el => el.examApplicationId === selectedApplicant.examApplicationId || el.applicantId === selectedApplicant.applicantId);

            if (applicant) {
                setSelectedApplicant(applicant);
            }
        }
    }, [examApplicationsList]);

    const handleOpenSignExamModal = () => {
        sendCode();
        setShowSendSms(true);
    };

    const handleSubmitCode = (code: string, captcha?: string) => {
        props.approveExam({ examId, stage: 1, body: { code, captcha } }).then(success => {
            setShowSendSms(!success);
            success && onClickBack();
        });
    };

    const onClickBack = () => {
        const urlParams = new URLSearchParams(window.location.search);
        const from = urlParams.get('from');

        if (from) {
            push(`/${from}`);
        } else {
            goBack();
        }
    };

    const renderApprove = examDetails && examDetails.status === ExamStatus.AwaitingExpertConfirmation && auth && !examDetails.examCreationProgress.find(p => p.expertId === auth.profile.id);

    const renderApproveExamStage = renderApprove && auth ? (ed: IExamDetails) => {
        return (
          <>
              <SmsSignModal
                onSubmit={handleSubmitCode}
                onSendPasswordAgain={sendCode}
                processing={sendSmsProcessing}
                onHide={() => setShowSendSms(false)}
                show={showSendSms}
                phone={auth.profile.phone}
                apiError={apiError}
                waitingSeconds={smsCodeStatus && smsCodeStatus.waitingSeconds}
                title="Подтверждение мероприятия"
              />
          </>
        );
    } : undefined;

    const cardHeaderBtn = () => (
        <Button onClick={() => handleOpenSignExamModal()}>
            Подтвердить проведение
        </Button>
    );

    return examDetails ? (
      <Card
        title={examDetails.qualificationTitle}
        onClickBack={onClickBack}
        apiError={apiError}
        show404Error={!examDetails}
        showLoader={processing}
        smallTitle
        withBackButton
        withoutPadding
        withoutTitleMargin
        headerChild={renderApprove ? cardHeaderBtn() : undefined}
      >
          {renderApprove ? (
                  <ExamDetailsConfirmationStatus
                      examDetails={examDetails}
                      error={apiError}
                      renderAppendForInfo={renderApproveExamStage}
                  />
              ) : (
                <ExamDetailsTabs
                  readonly
                  tab="info"
                  examDetails={examDetails}
                  examApplicationsList={examApplicationsList || undefined}
                  applicantsTabProps={{
                      handleApplicantsListChange: onFetchExamApplications,
                      handleApplicantClick: () => true,
                  }}
                  error={apiError}
                  examUnAssignedApplicationsList={{
                      fetch: props.fetchExamUnAssignedApplicationsList,
                      clear: props.clearExamUnAssignedApplicationsList,
                      info: props.examUnAssignedApplicationsList,
                  }}
                  expertsForDayList={{
                      fetch: props.getExpertsForDayAsync,
                      clear: props.clearExpertsForDayList,
                      info: props.expertsForDayList,
                  }}
                  examApplicationsForDay={{
                      fetch: props.fetchExamApplicationsForDayAsync,
                      clear: props.clearExamApplicationsForDay,
                      info: props.examApplicationsForDayList,
                  }}
                  experts={{
                      fetch: props.getExpertsAsync,
                      clear: props.clearExpertsList,
                      info: props.experts,
                  }}
                  unratedExamApplications={{
                      fetch: props.fetchUnratedExamApplications,
                      clear: props.clearUnratedExamApplicationsList,
                      info: props.unratedExamApplicationsList,
                  }}
                  applicantDetails={{
                      fetch: props.fetchExamApplicationDetails,
                      clear: props.clearExamApplicationDetails,
                      info: props.examApplicationDetails,
                  }}
                  examApplicationsForDayAdmit={{
                      fetch: props.fetchExamApplicationsForDayAdmitAsync,
                      clear: props.clearExamApplicationsListForAdmit,
                      info: props.examApplicationsListForAdmit,
                  }}
                />)}
      </Card>
    ) : null;
};

const mapState = (state: IRootState) => ({
    examDetails: state.exam.examDetails,
    smsCodeStatus: state.exam.smsCodeStatus,
    examApplicationsList: state.examApplications.examApplicationsList,
    auth: state.auth,
    apiError: state.errors.error,
    examUnAssignedApplicationsList: state.examApplications.examUnAssignedApplicationsList,
    expertsForDayList: state.experts.expertsForDayList,
    experts: state.experts.expertsList,
    examApplicationsForDayList: state.examApplications.examApplicationsForDayList,
    unratedExamApplicationsList: state.examApplications.unratedExamApplicationsList,
    examApplicationDetails: state.examApplications.examApplicationDetails,
    examApplicationsListForAdmit: state.examApplications.examApplicationsListForAdmit,
});

const mapDispatch = (dispatch: Dispatch) => ({
    onClearExamApplications: dispatch.examApplications.clearExamApplications,
    onFetchExamApplications: dispatch.examApplications.fetchExamApplicationsAsync,
    onClearExamDetails: dispatch.exam.clearExamDetails,
    onFetchExamDetails: dispatch.exam.fetchExamDetailsAsync,
    setErrorHandler: dispatch.errors.setErrorHandler,
    sendCodeForApproveExam: dispatch.exam.sendCodeForApproveExam,
    approveExam: dispatch.exam.approveExam,

    clearExamUnAssignedApplicationsList: dispatch.examApplications.clearExamUnAssignedApplicationsList,
    fetchExamUnAssignedApplicationsList: dispatch.examApplications.fetchExamUnAssignedApplicationsList,

    getExpertsForDayAsync: dispatch.experts.getExpertsForDayAsync,
    clearExpertsForDayList: dispatch.experts.clearExpertsForDayList,

    fetchExamApplicationsForDayAsync: dispatch.examApplications.fetchExamApplicationsForDayAsync,
    clearExamApplicationsForDay: dispatch.examApplications.clearExamApplicationsForDay,

    getExpertsAsync: dispatch.experts.getExpertsAsync,
    clearExpertsList: dispatch.experts.clearExpertsList,

    fetchUnratedExamApplications: dispatch.examApplications.fetchUnratedExamApplications,
    clearUnratedExamApplicationsList: dispatch.examApplications.clearUnratedExamApplicationsList,

    fetchExamApplicationDetails: dispatch.examApplications.fetchExamApplicationDetails,
    clearExamApplicationDetails: dispatch.examApplications.clearDetails,

    fetchExamApplicationsForDayAdmitAsync: dispatch.examApplications.fetchExamApplicationsForDayAdmitAsync,
    clearExamApplicationsListForAdmit: dispatch.examApplications.clearExamApplicationsListForAdmit,
});

export default connect(mapState, mapDispatch)(ExamDetailsContainer);

interface ExamsDetailsOwnProps {
    examId: string;
}

type connectedProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>

type Props = connectedProps & ExamsDetailsOwnProps;
